/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {getSpreaderSensorApps, Manufacturer} from '@ion/core/src/entities/Manufacturer';
import {transformSpreaderData} from '@ion/core/src/entities/Spreader';
import {getSensorData} from '@ion/core/src/services/MicroServices';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseSpreaderResult = UseDefaultQueryResult & {
  spreaders: any;
};

export interface UseSpreaderParams {
  id: string;
  sensorApps: string[];
};

/**
 * Retrieves the device sensor details for the given device esn id.
 * Sensor apps can be DICKEY_JOHN/FORCE_AMERICA/CIRUS
 * Each device can only have only sensor type reporting data.
 * The hook will retrieve latest 4 results.
 *
 * @param id of the device to retrieve details for
 * @param sensorApps SensorTypes
 * @param enabled if the device is enabled / disabled
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns a query result
 */
export const useSpreaderData = (
  {
    id,
    sensorApps = getSpreaderSensorApps()
  }: UseSpreaderParams,
  enabled = false,
  staleTime = getStaleTime(1, false, false)
): UseSpreaderResult => {
  const query = useQuery([QueryKey.DEVICE_GET, id, sensorApps], async() => {
    const latest = await getSensorData({
      mobileId        : id,
      apps            : [...sensorApps, Manufacturer.ROADWATCH].join(', '),
      numberOfRecords : 4
    });
    return latest;
  }, {
    staleTime : staleTime as number,
    enabled
  });

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  return createQueryResult(query, {
    spreaders : transformSpreaderData(query.data)
  }) as UseSpreaderResult;
};

export default useSpreaderData;
